<template>
  <header
    :class="[
      'base-header',
      { active: isOpen, 'is-hidden': isHidden && !isOpen },
    ]"
  >
    <div class="wrapper">
      <div class="base-header--left">
        <nuxt-link :to="localePath('/')">
          <base-icon name="Logo_light" class="logo" />
        </nuxt-link>
      </div>

      <div class="base-header--right">
        <nav
          class="base-header--nav"
          :class="{ 'nav-longword': locale !== 'en' }"
        >
          <nuxt-link
            class="text-body-2 text-black-monochrome"
            :to="localePath('directory')"
            >{{ $t("header.h_dir") }}</nuxt-link
          >
          <nuxt-link
            class="text-body-2 text-black-monochrome"
            :to="localePath('/directory/map')"
            >{{ $t("header.h_map") }}</nuxt-link
          >
        </nav>
        <base-language-button />
      </div>

      <button
        :class="['base-header--menu-button', { active: isOpen }]"
        @click="toggleMenu"
      >
        <span />
      </button>
    </div>
    <div
      :class="['base-header--overlay', { active: isOpen }]"
      @click="toggleMenu"
    >
      <nav
        class="base-header--popup"
        :class="{ 'base-header--popup-active': isOpen }"
      >
        <nuxt-link
          class="font--h5 text-black-monochrome"
          :to="localePath('directory')"
          >{{ $t("header.h_dir") }}</nuxt-link
        >
        <nuxt-link
          class="font--h5 text-black-monochrome"
          :to="localePath('/directory/map')"
          >{{ $t("header.h_map") }}</nuxt-link
        >
        <div class="auth-controls">
          <base-language-button />
        </div>
      </nav>
    </div>
  </header>
</template>

<script setup lang="ts">
const localePath = useLocalePath();
const { locale } = useI18n();

const isOpen = ref(false);
const isHidden = ref(false);

function toggleMenu() {
  isOpen.value = !isOpen.value;
  if (isOpen.value) isHidden.value = false;
}

const SCROLL_THRESHOLD = 8;
const REVEAL_NEAR_TOP = 60;
let lastScrollY = 0;
let ticking = false;

function onScroll() {
  if (ticking) return;
  ticking = true;
  requestAnimationFrame(() => {
    const y = window.scrollY;
    const delta = y - lastScrollY;

    if (y < REVEAL_NEAR_TOP) {
      isHidden.value = false;
    } else if (delta > SCROLL_THRESHOLD) {
      isHidden.value = true;
    } else if (delta < -SCROLL_THRESHOLD) {
      isHidden.value = false;
    }

    lastScrollY = y;
    ticking = false;
  });
}

onMounted(() => {
  lastScrollY = window.scrollY;
  window.addEventListener("scroll", onScroll, { passive: true });
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll);
});
</script>

<style scoped lang="scss">
.base-header {
  position: relative;
  top: 0;
  left: 0;
  width: 100%;
  min-width: 500px;
  height: 66px;
  background-color: var(--header-bg);

  .wrapper {
    max-width: 1440px;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    margin: auto;
    box-sizing: border-box;
    .logo {
      width: 164px;
      height: 20px;
      :deep(.icon) {
        width: 164px;
        height: 20px;
        svg {
          width: 164px;
          height: 20px;
        }
      }
    }
  }
  .nav-longword {
    gap: 20px;
  }
  &--left {
    display: flex;
    align-items: center;
  }
  &--right {
    display: flex;
    align-items: center;
    gap: 24px;
  }
  &--nav {
    display: flex;
    align-items: center;
    gap: 24px;
    a {
      text-decoration: none;
      display: flex;
      flex-direction: row;
      align-items: center;
      position: relative;
      transition: scale 0.3s ease;
      &:hover {
        scale: 1.05;
      }
      &:active {
        scale: 0.95;
      }
    }
  }
  &--menu-button {
    display: none;
  }
  &--overlay {
    // display: none;
    height: 0;
    overflow: hidden;
    height: 0px;
    will-change: height;
    transition: height 0.3s ease;
  }
}

@media (max-width: 60rem) {
  .base-header {
    min-width: unset;
    height: 100%;
    border-bottom: unset;
    position: sticky;
    top: 0;
    z-index: 100;
    border-radius: 0 0 12px 12px;
    transition: transform 0.3s ease;
    will-change: transform;
    &.is-hidden {
      transform: translateY(-100%);
    }
    &.active {
      border-radius: 0;
    }
    .wrapper {
      height: 70px;
      padding: 19px 24px;
      display: flex;
      justify-content: space-between;
    }
    &--left {
      gap: 0;
    }
    &--nav {
      display: none;
    }
    &--right {
      display: none;
    }
    &--menu-button {
      all: unset;
      cursor: pointer;
      width: 32px;
      height: 32px;
      padding: 6px 3px;
      box-sizing: border-box;
      span,
      span::before,
      span::after {
        position: absolute;
        width: 26px;
        height: 2px;
        background-color: var(--black-monochrome);
        transition: transform 0.2s ease-in-out;
      }
      span::before {
        content: "";
        top: -10px;
      }
      span::after {
        content: "";
        top: 10px;
      }
    }
    &--menu-button.active {
      span {
        transform: rotate(45deg);
      }
      span::before {
        opacity: 0;
      }
      span::after {
        top: 0;
        transform: rotate(90deg);
      }
    }
    &--overlay.active {
      position: fixed;
      top: 70px;
      left: 0;
      width: 100dvw;
      height: calc(100dvh - 70px);
      background-color: var(--header-bg);
      display: flex;
    }
    &.active {
      position: fixed;
      top: 0;
      left: 0;
      z-index: 9999;
    }
    &--popup {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding-top: 68px;
      margin: 0 auto;
      gap: 64px;
      width: 100%;
      padding-left: 24px;
      padding-right: 24px;
      padding-top: 60px;
      padding-bottom: 32px;
      overflow: hidden;
      height: 0px;
      will-change: height;
      transition: height 0.3s ease;
      &-active {
        height: calc(100dvh - 70px);
      }

      .auth-controls {
        display: flex;
        flex-direction: column;
        align-items: center;
        margin-top: auto;
        width: 100%;
        gap: 16px;
        .base-button {
          width: 100%;
        }
      }

      a {
        text-decoration: none;
        display: flex;
        flex-direction: row;
        align-items: center;
      }
    }
  }
}
</style>
